import { CarrierSearch, ShipperSearch } from '@types';
import { JWT } from '@state';
import { useRecoilValue } from 'recoil';
import { useIsInRoute } from '@hooks';
import { canImpersonateFromJwt } from './useImpersonation.helpers';
import { useImpersonateShipper } from './useImpersonateShipper';
import { useImpersonateCarrier } from './useImpersonateCarrier';

// Re-exported so existing consumers can keep importing it from this module.
export { IMPERSONATION_LOCAL_STORAGE_KEY } from './useImpersonation.helpers';

export const useImpersonation = () => {
	const jwt = useRecoilValue(JWT);
	const carrierInRoute = useIsInRoute('carrier');
	const shipperInRoute = useIsInRoute('shipper');
	const { resolveShipperFromRoute, impersonateShipper, clearShipper, refrainShipper, } = useImpersonateShipper();
	const { resolveCarrierFromRoute, impersonateCarrier, clearCarrier, refrainCarrier, } = useImpersonateCarrier();

	// Gating now lives in the per-domain hooks so direct callers can't bypass it.
	const canImpersonate = () => canImpersonateFromJwt(jwt);

	const impersonate = async (
		userType: 'carrier' | 'shipper' | null = null,
		impersonation: ShipperSearch | CarrierSearch | number | string | null = null
	) => {
		if (!userType) {
			if (shipperInRoute) {
				await resolveShipperFromRoute();
			} else if (carrierInRoute) {
				await resolveCarrierFromRoute();
			}
		} else if (userType === 'carrier' && impersonation) {
			await impersonateCarrier(impersonation as CarrierSearch | number | string);
		} else if (userType === 'shipper' && impersonation) {
			await impersonateShipper(impersonation as ShipperSearch | number | string);
		}
	}

	const refrain = (userType: 'carrier' | 'shipper' | null = null) => {
		if (userType === 'carrier') {
			refrainCarrier();
		} else if (userType === 'shipper') {
			refrainShipper();
		} else {
			clearShipper();
			clearCarrier();
		}
	}

	return {
		impersonate,
		refrain,
		canImpersonate,
	}
}
